import { Controller } from "@hotwired/stimulus"

/**
 * Контроллер modal_tabs — drawer с вкладками subcomponents.
 *
 * При открытии для новой строки (другой pk) — сбрасывает кеш
 * и перезагружает данные вкладок.
 */
export default class extends Controller {
  static targets = ["drawer", "overlay", "tabBtn", "tabContent"]
  static values = { componentId: String, loadUrl: String }

  connect() {
    this._currentPk = null
    this._prefetchCache = {}
    this._frameLoadHandler = this._handleFrameLoad.bind(this)

    document.addEventListener("turbo:frame-load", this._frameLoadHandler)

    // === TELEPORT drawer & overlay into body ===
    // Grid-components create stacking context (position: relative + z-index).
    // A fixed child inside a stacking context is clipped by its ancestor,
    // so drawer can NOT appear above sibling grid-components (filter, table).
    // Moving drawer/overlay to body escapes all ancestor stacking contexts.
    this._drawer = this.hasDrawerTarget ? this.drawerTarget : null
    this._overlay = this.hasOverlayTarget ? this.overlayTarget : null
    this._teleported = false

    // Сохраняем ссылки на targets ДО телепорта — после append в body
    // Stimulus getters больше не найдут их внутри this.element.
    this._tabBtns = this.hasTabBtnTarget ? Array.from(this.tabBtnTargets) : []
    this._tabContents = this.hasTabContentTarget ? Array.from(this.tabContentTargets) : []

    if (this._drawer && this._overlay) {
      this._originalParent = this._drawer.parentNode
      this._originalNextSibling = this._drawer.nextSibling
      document.body.appendChild(this._drawer)
      document.body.appendChild(this._overlay)
      this._teleported = true
    }

    // Восстанавливаем Stimulus actions на телепортированных элементах
    // (data-action не работает вне this.element)
    this._drawerClickHandler = (event) => {
      const closeBtn = event.target.closest('[data-action*="dae20--modal-tabs#close"]')
      if (closeBtn) {
        this.close()
        return
      }
      const tabBtn = event.target.closest('[data-dae20--modal-tabs-target="tabBtn"]')
      if (tabBtn) {
        const tabId = tabBtn.dataset.tabId
        if (tabId) this._loadTab(tabId, this._currentPk)
      }
    }
    if (this._drawer) this._drawer.addEventListener('click', this._drawerClickHandler)

    this._overlayClickHandler = () => this.close()
    if (this._overlay) this._overlay.addEventListener('click', this._overlayClickHandler)

    this._injectButtons()
  }

  disconnect() {
    // Гарантируем очистку body-класса при удалении элемента (Turbo-навигация и т.д.)
    document.body.classList.remove("dae20-modal-open")

    if (this._frameLoadHandler) {
      document.removeEventListener("turbo:frame-load", this._frameLoadHandler)
    }

    // Return teleported elements to their original place (or remove if parent gone)
    if (this._teleported) {
      if (this._drawer && this._drawerClickHandler) {
        this._drawer.removeEventListener('click', this._drawerClickHandler)
      }
      if (this._overlay && this._overlayClickHandler) {
        this._overlay.removeEventListener('click', this._overlayClickHandler)
      }

      if (this._originalParent && this._originalParent.isConnected) {
        if (this._originalNextSibling && this._originalNextSibling.parentNode === this._originalParent) {
          this._originalParent.insertBefore(this._overlay, this._originalNextSibling)
          this._originalParent.insertBefore(this._drawer, this._originalNextSibling)
        } else {
          this._originalParent.appendChild(this._overlay)
          this._originalParent.appendChild(this._drawer)
        }
      } else {
        // Parent detached — safely remove from body
        if (this._drawer && this._drawer.parentNode) this._drawer.remove()
        if (this._overlay && this._overlay.parentNode) this._overlay.remove()
      }
    }
  }

  // === Public actions ===

  open(event) {
    event.stopPropagation()
    const pk = event.currentTarget.dataset.modalTabsPk
    if (pk) this._openDrawerForPk(pk)
  }

  // Prefetch — вызывается при mouseenter на кнопку карточки (Stimulus action)
  prefetch(event) {
    const btn = event.currentTarget
    if (btn.dataset.prefetched) return
    btn.dataset.prefetched = "1"
    const pk = btn.dataset.modalTabsPk
    if (pk) this._prefetch(pk)
  }

  close() {
    if (this._drawer) {
      this._drawer.classList.add("translate-x-full")
      this._drawer.classList.remove("translate-x-0")
    }
    if (this._overlay) this._overlay.classList.add("hidden")
    document.body.classList.remove("dae20-modal-open")
  }

  switchTab(event) {
    const tabId = event.currentTarget.dataset.tabId
    if (tabId) this._loadTab(tabId, this._currentPk)
  }

  // "Применить" — AJAX-сабмит формы, drawer остаётся открытым
  apply(event) {
    const btn = event.currentTarget
    const formId = btn.dataset.modalTabsFormId
    const subcomponentId = btn.dataset.modalTabsSubcomponentId
    const drillPk = btn.dataset.modalTabsDrillPk || ""

    const form = document.getElementById(formId)
    if (!form) return

    const formData = new FormData(form)
    formData.append("apply", "1")
    formData.append("subcomponent_id", subcomponentId)
    formData.append("drill_pk", drillPk)

    btn.disabled = true
    btn.textContent = "Сохранение..."

    fetch(form.action, {
      method: "POST",
      body: formData,
      headers: {
        "Accept": "text/vnd.turbo-stream.html",
        "X-Requested-With": "XMLHttpRequest"
      }
    })
      .then(r => r.text())
      .then(html => {
        Turbo.renderStreamMessage(html)
      })
      .catch(() => {
        const flashEl = document.getElementById(`subcomponent_flash_${subcomponentId}`)
        if (flashEl) {
          flashEl.className = "px-3 py-2 text-xs bg-red-50 text-red-700 border-red-300 border rounded mx-4 mt-2"
          flashEl.textContent = "Ошибка сети"
        }
      })
      .finally(() => {
        btn.disabled = false
        btn.textContent = "Применить"
      })
  }

  // === Вызывается после Turbo Stream обновлений (пагинация, live_table) ===

  tabContentTargetsConnected() {
    this._injectButtons()
  }

  // === Private ===

  _handleFrameLoad(event) {
    const frame = event.target
    if (!(frame instanceof Element)) return

    const expectedFrameIds = [
      `dae20_table_frame_${this.componentIdValue}`,
      `p220_table_frame_${this.componentIdValue}`
    ]

    if (!expectedFrameIds.includes(frame.id)) return
    if (!this.element.contains(frame)) return

    this._injectButtons()
  }

  _injectButtons() {
    const rows = this.element.querySelectorAll("[data-clickable-row-record-id-value]")
    rows.forEach(row => {
      const pk = row.dataset.clickableRowRecordIdValue
      if (!pk) return

      // Инжект кнопки-стрелки (если ещё нет)
      if (!row.querySelector("[data-modal-tabs-btn]")) {
        const btn = document.createElement("button")
        btn.type = "button"
        btn.setAttribute("data-modal-tabs-btn", "1")
        btn.setAttribute("data-modal-tabs-pk", pk)
        btn.setAttribute("data-action", "mouseenter->dae20--modal-tabs#prefetch click->dae20--modal-tabs#open")
        btn.title = "Открыть подкомпоненты"
        btn.className = "absolute right-1 top-1/2 -translate-y-1/2 z-10 p-1 rounded " +
          "bg-indigo-50 hover:bg-indigo-100 text-indigo-600 border border-indigo-200 shadow-sm text-xs"
        btn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" class="w-3.5 h-3.5" fill="none" ' +
          'viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" ' +
          'stroke-width="2" d="M9 5l7 7-7 7"/></svg>'

        btn.addEventListener("mouseenter", () => {
          this._prefetch(pk)
        }, { once: true })

        row.style.position = "relative"
        row.appendChild(btn)
      }

      // Привязка клика по строке (однократно)
      if (!row.dataset.modalTabsRowClickBound) {
        row.dataset.modalTabsRowClickBound = "1"
        row.style.cursor = "pointer"

        row.addEventListener("click", (event) => {
          // Не перехватывать клик по кнопке-стрелке (она обрабатывает сама)
          if (event.target.closest("[data-modal-tabs-btn]")) return
          // Не перехватывать клики по ссылкам, кнопкам, инпутам
          if (event.target.closest("a, button, input, textarea, select")) return
          // Не открывать drawer при выделении текста (для копирования)
          const sel = window.getSelection()
          if (sel && sel.toString().trim().length > 0) return

          this._openDrawerForPk(pk)
        })

        // Prefetch данных при наведении на строку
        row.addEventListener("mouseenter", () => {
          this._prefetch(pk)
        }, { once: true })
      }
    })
  }

  _openDrawerForPk(pk) {
    if (!this._drawer || !this._overlay) return

    const pkChanged = this._currentPk !== pk
    this._currentPk = pk

    // Если pk изменился — сбрасываем loaded-флаги всех вкладок
    if (pkChanged) {
      this._tabContents.forEach(el => {
        delete el.dataset.loaded
      })
    }

    // Показываем drawer + overlay
    this._drawer.classList.remove("translate-x-full")
    this._drawer.classList.add("translate-x-0")
    this._overlay.classList.remove("hidden")
    document.body.classList.add("dae20-modal-open")

    // Загружаем первую вкладку
    if (this._tabBtns.length > 0) {
      this._loadTab(this._tabBtns[0].dataset.tabId, pk)
    }
  }

  _loadTab(tabId, pk) {
    // Переключаем видимость вкладок
    this._tabContents.forEach(el => {
      if (el.dataset.tabId === tabId) {
        el.classList.remove("hidden")
      } else {
        el.classList.add("hidden")
      }
    })

    // Переключаем стили кнопок
    this._tabBtns.forEach(btn => {
      if (btn.dataset.tabId === tabId) {
        btn.classList.add("bg-white", "font-semibold", "text-blue-700", "border-blue-300")
        btn.classList.remove("bg-gray-100", "text-gray-600")
      } else {
        btn.classList.remove("bg-white", "font-semibold", "text-blue-700", "border-blue-300")
        btn.classList.add("bg-gray-100", "text-gray-600")
      }
    })

    // Находим контент вкладки
    const contentEl = this._tabContents.find(el => el.dataset.tabId === tabId)
    if (!contentEl) return

    // Если уже загружена для текущего pk — не перезагружаем
    if (contentEl.dataset.loaded === "1") return

    const drillPk = pk || ""
    const cacheKey = `${tabId}:${drillPk}`

    // Используем prefetch-кеш если данные уже загружены при hover
    if (this._prefetchCache[cacheKey]) {
      Turbo.renderStreamMessage(this._prefetchCache[cacheKey])
      contentEl.dataset.loaded = "1"
      delete this._prefetchCache[cacheKey]
      return
    }

    // Показываем спиннер пока данные загружаются
    const frame = contentEl.querySelector('turbo-frame')
    if (frame) {
      frame.innerHTML = `
        <div class="flex items-center justify-center py-12 text-gray-400">
          <svg class="animate-spin h-6 w-6 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span class="text-sm">Загрузка...</span>
        </div>`
    }

    const url = `${this.loadUrlValue}?subcomponent_id=${tabId}&drill_pk=${encodeURIComponent(drillPk)}&format=turbo_stream`

    fetch(url, {
      headers: {
        "Accept": "text/vnd.turbo-stream.html",
        "X-Requested-With": "XMLHttpRequest",
        "X-CSRF-Token": document.querySelector("meta[name=csrf-token]")?.content || ""
      }
    })
      .then(r => r.text())
      .then(html => {
        Turbo.renderStreamMessage(html)
        contentEl.dataset.loaded = "1"
      })
  }

  _prefetch(pk) {
    const tabId = this._tabBtns[0]?.dataset.tabId
    if (!tabId) return

    const drillPk = pk || ""
    const cacheKey = `${tabId}:${drillPk}`
    const url = `${this.loadUrlValue}?subcomponent_id=${tabId}&drill_pk=${encodeURIComponent(drillPk)}&format=turbo_stream`

    fetch(url, {
      headers: {
        "Accept": "text/vnd.turbo-stream.html",
        "X-Requested-With": "XMLHttpRequest",
        "X-CSRF-Token": document.querySelector("meta[name=csrf-token]")?.content || ""
      }
    })
      .then(r => r.text())
      .then(html => {
        this._prefetchCache[cacheKey] = html
      })
  }

}
